import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';
import { setCurrentUser, setIsAuthenticated } from 'src/redux/auth.slice';
import { RootState } from 'src/redux/store';
import { saveUserBasicDetails } from 'src/services/AuthService';

import ButtonCmp from '../../components/ButtonCmp';
import WalToast from '../../utils/WalToast';
import AuthWrapper from '../AppLayout/AuthWrapper';

import PersonalInfo, { PersonalInfoFormData } from './steps/PersonalInfo';
import ShippingProfile, { ShippingProfileFormData } from './steps/ShippingProfile';

const TOTAL_STEPS = 2;

interface PersonalInfoState {
    data: PersonalInfoFormData;
    // Cropped base64 image chosen in step 1 — uploaded only on final submit.
    imageUrl: string;
}

/**
 * /setup-account — two-step onboarding for portal contacts (isSetBasicDetail = false).
 * Step 1 (PersonalInfo) only validates and hands its data up; step 2 (ShippingProfile)
 * triggers the single PUT /users/update-user-details carrying both steps.
 */
const SetupAccount = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);

    const [currentStep, setCurrentStep] = useState<1 | 2>(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [personalInfo, setPersonalInfo] = useState<PersonalInfoState | null>(null);
    const [shippingProfile, setShippingProfile] = useState<ShippingProfileFormData | undefined>(undefined);

    const formRefs = {
        personalInfoForm: useRef<HTMLFormElement>(null),
        shippingProfileForm: useRef<HTMLFormElement>(null),
    };

    const handlePersonalInfoSubmit = (data: PersonalInfoFormData, imageUrl: string) => {
        setPersonalInfo({ data, imageUrl });
        // Step 2 reports its own validity via setIsFormValid once it mounts.
        setIsFormValid(false);
        setCurrentStep(2);
    };

    const handleFinalSubmit = async (answers: ShippingProfileFormData) => {
        if (!personalInfo) {
            // Should be unreachable — step 2 is only rendered after step 1 submits.
            setCurrentStep(1);

            return;
        }

        setShippingProfile(answers);

        try {
            setIsLoading(true);

            const { data, imageUrl } = personalInfo;
            const formData = new FormData();
            formData.append('firstName', data.firstName ?? '');
            formData.append('lastName', data.lastName ?? '');
            formData.append('title', data.title ?? '');
            formData.append('phone', data.phone ?? '');
            formData.append('language', data.defaultLanguage ?? '');

            formData.append('erpSystem', answers.erpSystem);
            if (answers.erpSystemOther) formData.append('erpSystemOther', answers.erpSystemOther);
            // Multipart carries booleans as strings; BE normalizes 'true'/'false'.
            formData.append('erpSyncInterest', String(Boolean(answers.erpSyncInterest)));
            formData.append('monthlyShipmentVolume', answers.monthlyShipmentVolume);
            formData.append('shippingMode', answers.shippingMode);
            formData.append('laneRegularity', answers.laneRegularity);
            formData.append('crossBorder', answers.crossBorder);

            if (imageUrl) {
                const base64Response = await fetch(imageUrl);
                const blob = await base64Response.blob();
                formData.append('profileImage', blob, 'profile.jpg');
            }

            const response = await saveUserBasicDetails(formData);
            const responseData = response?.data;

            if (!responseData) {
                WalToast.error(t('Common.Something_went_wrong'));

                return;
            }

            dispatch(
                setCurrentUser({
                    ...currentUser,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    title: data.title,
                    phone: data.phone,
                    language: data.defaultLanguage,
                    ...(responseData?.image && { image: responseData.image }),
                    ...(responseData?.imageUrl && { imageUrl: responseData.imageUrl }),
                    erpSystem: answers.erpSystem,
                    erpSystemOther: answers.erpSystemOther || null,
                    erpSyncInterest: Boolean(answers.erpSyncInterest),
                    monthlyShipmentVolume: answers.monthlyShipmentVolume,
                    shippingMode: answers.shippingMode,
                    laneRegularity: answers.laneRegularity,
                    crossBorder: answers.crossBorder,
                    isActive: true,
                    isSetBasicDetail: true,
                })
            );
            navigate(ROUTES.HOME, { replace: true });
        } catch (error: any) {
            console.error('Error saving setup account details:', error);
            WalToast.error(error?.response?.data?.message || t('Common.Something_went_wrong'));
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrevious = () => {
        if (currentStep === 2) {
            // Keep personalInfo so step 1 re-renders with the user's edits.
            setCurrentStep(1);

            return;
        }

        localStorage.removeItem('token');
        dispatch(setIsAuthenticated(false) as any);
        dispatch(setCurrentUser(null) as any);
        navigate(ROUTES.LOGIN);
    };

    const getCurrentFormRef = () => (currentStep === 1 ? formRefs.personalInfoForm : formRefs.shippingProfileForm);

    const handleNextClick = () => {
        const currentFormRef = getCurrentFormRef();

        if (currentFormRef?.current) {
            currentFormRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
    };

    const stepI18n = currentStep === 1 ? 'SetupAccount.PersonalInfo' : 'SetupAccount.ShippingProfile';

    return (
        <AuthWrapper authHeading={t(`${stepI18n}.Title`)} authSubTitle={t(`${stepI18n}.Subtitle`)}>
            <div className="flex-1 mt-[-12px]">
                {currentStep === 1 ? (
                    <PersonalInfo
                        ref={formRefs.personalInfoForm}
                        onSubmit={handlePersonalInfoSubmit}
                        initialValues={personalInfo?.data}
                        initialImageUrl={personalInfo?.imageUrl}
                        isLoading={isLoading}
                        setIsFormValid={setIsFormValid}
                    />
                ) : (
                    <ShippingProfile ref={formRefs.shippingProfileForm} onSubmit={handleFinalSubmit} initialValues={shippingProfile} isLoading={isLoading} setIsFormValid={setIsFormValid} />
                )}

                <div className="mt-5 flex items-center justify-between gap-x-4 pb-4">
                    <p className="text-xs font-medium text-gray500">{t('SetupAccount.LeftSideDetails.Step_Of', { step: currentStep, total: TOTAL_STEPS })}</p>

                    <div className="flex items-center gap-x-2">
                        <ButtonCmp onClick={handlePrevious} size="md" className="btn_secondary_black" disabled={isLoading}>
                            {t('Common.Back')}
                        </ButtonCmp>

                        <ButtonCmp onClick={handleNextClick} size="md" className="btn_primary_black" disabled={isLoading || !isFormValid} loading={isLoading}>
                            {t('Common.Continue')}
                        </ButtonCmp>
                    </div>
                </div>
            </div>
        </AuthWrapper>
    );
};

export default SetupAccount;
